import { createClient } from '@connectrpc/connect'
import { Code, ConnectError } from '@connectrpc/connect'
import {
  keepPreviousData,
  type QueryKey,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'

import type { PathItem } from '@jotta/grpc-connect-openapi/file'
import { SearchService } from '@jotta/grpc-connect-openapi/search'
import { queryClient } from '@jotta/query'
import type { AppError } from '@jotta/types/AppError'

import { authTransport } from '../transport'

import { useFileSubscription } from '../useSubscription'

type Opts<TSelected> = Omit<
  UseQueryOptions<PathItem[], AppError, TSelected, QueryKey>,
  'queryKey' | 'queryFn'
>

const searchClient = createClient(SearchService, authTransport)

function isTimeout(err: AppError): boolean {
  if (err.status === 504 || err.status === 408) {
    return true
  }

  if (err.grpcStatus === Code.DeadlineExceeded) {
    return true
  }
  if (
    err.cause instanceof ConnectError &&
    err.cause.code === Code.DeadlineExceeded
  ) {
    return true
  }

  return false
}

export function useFileSearch<TSelected = PathItem[]>(
  query: string,
  options?: Opts<TSelected>,
) {
  const queryKey = ['file_search', query]

  useFileSubscription(() => {
    queryClient.invalidateQueries({ queryKey })
  })
  return useQuery<PathItem[], AppError, TSelected>({
    queryKey,
    queryFn: async ({ signal }) => {
      const searchResponses = searchClient.search(
        { query },
        { signal, timeoutMs: 20_000 },
      )

      const results: PathItem[] = []
      for await (const response of searchResponses) {
        const match = response.match
        if (match) {
          results.push(match)

          // Stream partials but never overwrite a fuller cache
          queryClient.setQueryData<PathItem[]>(queryKey, old => {
            const ok = !old || old.every(val => results.includes(val))
            const next = ok ? [...results] : old
            return next
          })
        }
      }

      return results
    },

    placeholderData: keepPreviousData,

    throwOnError: false,

    retry: (failureCount, err) => !isTimeout(err) && failureCount < 2,

    ...options,
  })
}
